import type { IObservationRating } from "src/app/cbta-web/services/offline-rating-calculation.service";
import {
	DEFAULT_UPLIFT_LOOKUP_RULES,
	resolveUplift,
	type UpliftLookupRule,
} from "./uplift-lookup.defaults";

export interface CompetencyEICounts {
	e: number;
	i: number;
	ae: number;
	ai: number;
}

export interface CompetencyRatingPair {
	obCategoryCode: string;
	ratingA: number;
	ratingB: number;
}

export interface CompetencyRatingDetails {
	pairs: CompetencyRatingPair[];
	howOftenCounts: Map<string, CompetencyEICounts>;
}

const EMPTY_EI_COUNTS: CompetencyEICounts = { e: 0, i: 0, ae: 0, ai: 0 };

function hasPredefinedForm(
	competency: string,
	predefinedCountByCompetency: Map<string, number>,
): boolean {
	return (predefinedCountByCompetency.get(competency) ?? 0) > 0;
}

export function roundCompetencyScore(score: number): number {
	const frac = Math.round((score - Math.floor(score)) * 1000) / 1000;
	return frac >= 0.61 ? Math.ceil(score) : Math.floor(score);
}

export function calculateBaseScoreFromEI(effective: number, ineffective: number): number {
	if (effective + ineffective <= 0) return 0;
	const r = (effective + 1) / (effective + ineffective + 3);
	const raw = 4 * r + 1;
	return Math.min(4, roundCompetencyScore(raw));
}

export function calculateFinalCompetencyScore(
	baseScore: number,
	effectiveAdded: number,
	ineffectiveAdded: number,
	rules: UpliftLookupRule[] = DEFAULT_UPLIFT_LOOKUP_RULES,
): number {
	if (baseScore <= 0 && effectiveAdded + ineffectiveAdded <= 0) return 0;
	return Math.min(5, baseScore + resolveUplift(effectiveAdded, ineffectiveAdded, rules));
}

/** @deprecated Use calculateFinalCompetencyScore. */
export const calculateRatingBFromBase = calculateFinalCompetencyScore;

function competencyCodeFromObservationCode(observationCode: string): string {
	return observationCode.split("_")[0].toUpperCase();
}

function howOftenObservationKey(obs: {
	maneuverCode: string;
	observationCode: string;
	candidateRole?: string | null;
}): string {
	const role = (obs.candidateRole ?? "").trim();
	if (role) {
		return `${obs.maneuverCode}::${obs.observationCode}::${role}`;
	}
	return `${obs.maneuverCode}::${obs.observationCode}`;
}

/** Role-scoped predefined key (maneuver ignored). */
export function roleScopedKey(obs: {
	observationCode: string;
	candidateRole?: string | null;
}): string {
	const role = (obs.candidateRole ?? "").trim();
	return role ? `${obs.observationCode}::${role}` : obs.observationCode;
}

export function buildPredefinedRoleKeys(predefinedObs: IObservationRating[]): Set<string> {
	return new Set(predefinedObs.map(roleScopedKey));
}

export function isPredefinedForRole(
	obs: IObservationRating,
	predefinedRoleKeys: Set<string>,
): boolean {
	return predefinedRoleKeys.has(roleScopedKey(obs));
}

export function buildStep2ObservationCodes(
	newObs: IObservationRating[],
	predefinedRoleKeys: Set<string>,
	predefinedCountByCompetency: Map<string, number>,
): Set<string> {
	const codes = new Set<string>();
	for (const obs of newObs) {
		const active = obs.activeAttempt ?? 0;
		const total = obs.totalAttempt ?? 0;
		if (active <= 0 && total <= 0) continue;
		const competency = competencyCodeFromObservationCode(obs.observationCode);
		if ((predefinedCountByCompetency.get(competency) ?? 0) <= 0) continue;
		if (!isPredefinedForRole(obs, predefinedRoleKeys)) {
			codes.add(obs.observationCode);
		}
	}
	return codes;
}

/** All observation codes on the admin predefined form (any role / totalAttempt). */
export function buildFormObservationCodes(predefinedObs: IObservationRating[]): Set<string> {
	const codes = new Set<string>();
	for (const obs of predefinedObs) {
		codes.add(obs.observationCode);
	}
	return codes;
}

/** @deprecated Use buildFormObservationCodes. */
export const buildCoreObservationCodes = buildFormObservationCodes;

function buildPredefinedCountByCompetency(predefinedObs: IObservationRating[]): Map<string, number> {
	const codesByCompetency = new Map<string, Set<string>>();
	for (const obs of predefinedObs) {
		const competency = competencyCodeFromObservationCode(obs.observationCode);
		const codes = codesByCompetency.get(competency) ?? new Set<string>();
		codes.add(obs.observationCode);
		codesByCompetency.set(competency, codes);
	}
	const counts = new Map<string, number>();
	for (const [competency, codes] of codesByCompetency) {
		counts.set(competency, codes.size);
	}
	return counts;
}

interface ResolvedObAttempt {
	observationCode: string;
	maneuverCode: string;
	howManyActive: number;
	howOftenActive: number;
	total: number;
	predefinedForRole: boolean;
	predefinedInstance: boolean;
}

function collectResolvedAttempts(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	predefinedRoleKeys: Set<string>;
}): ResolvedObAttempt[] {
	const { predefinedObs, newObs, predefinedRoleKeys } = params;
	const newObsMap = new Map<string, IObservationRating>();
	for (const obs of newObs) {
		newObsMap.set(howOftenObservationKey(obs), obs);
	}

	const rows: ResolvedObAttempt[] = [];
	const seen = new Set<string>();

	for (const obs of predefinedObs) {
		const key = howOftenObservationKey(obs);
		seen.add(key);
		const entry = newObsMap.get(key);
		const active = entry ? (entry.activeAttempt ?? 0) : (obs.activeAttempt ?? 0);
		const total = entry ? (entry.totalAttempt ?? 0) : (obs.totalAttempt ?? 0);
		rows.push({
			observationCode: obs.observationCode,
			maneuverCode: obs.maneuverCode,
			howManyActive: active,
			howOftenActive: entry ? active : total,
			total,
			predefinedForRole: true,
			predefinedInstance: true,
		});
	}

	for (const obs of newObs) {
		const key = howOftenObservationKey(obs);
		if (seen.has(key)) continue;
		seen.add(key);
		const active = obs.activeAttempt ?? 0;
		const total = obs.totalAttempt ?? 0;
		rows.push({
			observationCode: obs.observationCode,
			maneuverCode: obs.maneuverCode,
			howManyActive: active,
			howOftenActive: active,
			total,
			predefinedForRole: isPredefinedForRole(obs, predefinedRoleKeys),
			predefinedInstance: false,
		});
	}

	return rows;
}

function groupAttemptsByCode(rows: ResolvedObAttempt[]): Map<string, ResolvedObAttempt[]> {
	const byCode = new Map<string, ResolvedObAttempt[]>();
	for (const row of rows) {
		const group = byCode.get(row.observationCode) ?? [];
		group.push(row);
		byCode.set(row.observationCode, group);
	}
	return byCode;
}

function hasAdditionalManeuverScore(rows: ResolvedObAttempt[]): boolean {
	const scoreByManeuver = new Map<string, number>();
	for (const row of rows) {
		if (row.predefinedInstance) continue;
		scoreByManeuver.set(
			row.maneuverCode,
			(scoreByManeuver.get(row.maneuverCode) ?? 0) + row.howManyActive,
		);
	}
	for (const score of scoreByManeuver.values()) {
		if (score > 0) return true;
	}
	return false;
}

function buildRatingASlots(params: {
	rows: ResolvedObAttempt[];
	predefinedCountByCompetency: Map<string, number>;
}): { competency: string; isCore: boolean; isObserved: boolean }[] {
	const { rows, predefinedCountByCompetency } = params;
	const slots: { competency: string; isCore: boolean; isObserved: boolean }[] = [];

	for (const [observationCode, codeRows] of groupAttemptsByCode(rows)) {
		const competency = competencyCodeFromObservationCode(observationCode);
		const competencyHasPredefined = (predefinedCountByCompetency.get(competency) ?? 0) > 0;

		if (!competencyHasPredefined) {
			let isObserved = false;
			let isAttempted = false;
			for (const row of codeRows) {
				if (row.howManyActive > 0) isObserved = true;
				if (row.howManyActive > 0 || row.total > 0) isAttempted = true;
			}
			if (!isObserved && !isAttempted) continue;
			slots.push({ competency, isCore: true, isObserved });
			continue;
		}

		const predefinedRows = codeRows.filter((row) => row.predefinedInstance);
		const observedPredefined = predefinedRows.reduce((sum, row) => sum + row.howManyActive, 0);
		const effective = predefinedRows.length > 0 && observedPredefined >= 1;

		if (predefinedRows.length > 0) {
			slots.push({ competency, isCore: true, isObserved: effective });
		}

		if (effective) continue;
		if (!hasAdditionalManeuverScore(codeRows)) continue;
		slots.push({ competency, isCore: false, isObserved: true });
	}

	return slots;
}

function buildCompetencyObservationContext(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	formObservationCodes: Set<string>;
}): {
	slots: { competency: string; isCore: boolean; isObserved: boolean }[];
	predefinedCountByCompetency: Map<string, number>;
	predefinedRoleKeys: Set<string>;
	step2ObservationCodes: Set<string>;
} {
	const { predefinedObs, newObs } = params;
	const predefinedCountByCompetency = buildPredefinedCountByCompetency(predefinedObs);
	const predefinedRoleKeys = buildPredefinedRoleKeys(predefinedObs);
	const step2ObservationCodes = buildStep2ObservationCodes(
		newObs,
		predefinedRoleKeys,
		predefinedCountByCompetency,
	);
	const rows = collectResolvedAttempts({ predefinedObs, newObs, predefinedRoleKeys });

	return {
		slots: buildRatingASlots({ rows, predefinedCountByCompetency }),
		predefinedCountByCompetency,
		predefinedRoleKeys,
		step2ObservationCodes,
	};
}

export function buildCompetencyObservationSlots(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	formObservationCodes: Set<string>;
}): { competency: string; isCore: boolean; isObserved: boolean }[] {
	const predefinedCountByCompetency = buildPredefinedCountByCompetency(params.predefinedObs);
	const predefinedRoleKeys = buildPredefinedRoleKeys(params.predefinedObs);
	const rows = collectResolvedAttempts({
		predefinedObs: params.predefinedObs,
		newObs: params.newObs,
		predefinedRoleKeys,
	});
	return buildRatingASlots({ rows, predefinedCountByCompetency });
}

function countEIByCompetencyHowMany(
	slots: { competency: string; isCore: boolean; isObserved: boolean }[],
): Map<string, CompetencyEICounts> {
	const counts = new Map<string, CompetencyEICounts>();
	for (const { competency, isCore, isObserved } of slots) {
		const bucket = counts.get(competency) ?? { e: 0, i: 0, ae: 0, ai: 0 };
		if (isCore) {
			if (isObserved) bucket.e++;
			else bucket.i++;
		} else if (isObserved) {
			bucket.ae++;
		} else {
			bucket.ai++;
		}
		counts.set(competency, bucket);
	}
	return counts;
}

function inactiveAttempts(active: number, total: number): number {
	return Math.max(0, total - active);
}

export function countEIByCompetencyHowOften(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	predefinedCountByCompetency: Map<string, number>;
	predefinedRoleKeys: Set<string>;
	step2ObservationCodes?: Set<string>;
}): Map<string, CompetencyEICounts> {
	const { predefinedObs, newObs, predefinedCountByCompetency, predefinedRoleKeys } = params;
	const counts = new Map<string, CompetencyEICounts>();
	const rows = collectResolvedAttempts({ predefinedObs, newObs, predefinedRoleKeys });

	const bump = (competency: string, field: keyof CompetencyEICounts, by: number) => {
		if (by <= 0) return;
		const bucket = counts.get(competency) ?? { e: 0, i: 0, ae: 0, ai: 0 };
		bucket[field] += by;
		counts.set(competency, bucket);
	};

	for (const [observationCode, codeRows] of groupAttemptsByCode(rows)) {
		const competency = competencyCodeFromObservationCode(observationCode);
		const competencyHasPredefined = (predefinedCountByCompetency.get(competency) ?? 0) > 0;

		for (const row of codeRows) {
			if (row.howOftenActive <= 0 && row.total <= 0) continue;
			if (!competencyHasPredefined || row.predefinedInstance) {
				bump(competency, "e", row.howOftenActive);
				bump(competency, "i", inactiveAttempts(row.howOftenActive, row.total));
				continue;
			}

			bump(competency, "ae", row.howOftenActive);
			bump(competency, "ai", inactiveAttempts(row.howOftenActive, row.total));
		}
	}

	return counts;
}

export function howOftenAttemptMetaFromCounts(counts: CompetencyEICounts): {
	totalAttempts: number;
	activeAttempts: number;
	completionPercentage: number;
	observationCount: number;
} {
	const activeAttempts = counts.e + counts.ae;
	const totalAttempts = counts.e + counts.i + counts.ae + counts.ai;
	const completionPercentage =
		totalAttempts > 0 ? +((activeAttempts / totalAttempts) * 100).toFixed(2) : 0;
	return {
		totalAttempts,
		activeAttempts,
		completionPercentage,
		observationCount: totalAttempts,
	};
}

function finalScoreFromCounts(
	competency: string,
	counts: CompetencyEICounts,
	predefinedCountByCompetency: Map<string, number>,
	rules: UpliftLookupRule[],
): number {
	const baseScore = calculateBaseScoreFromEI(counts.e, counts.i);
	return hasPredefinedForm(competency, predefinedCountByCompetency)
		? calculateFinalCompetencyScore(baseScore, counts.ae, counts.ai, rules)
		: baseScore;
}

export function calculateCompetencyRatingDetails(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	formObservationCodes: Set<string>;
	upliftRules?: UpliftLookupRule[];
}): CompetencyRatingDetails {
	const { predefinedObs, newObs, formObservationCodes, upliftRules } = params;
	const { slots, predefinedCountByCompetency, predefinedRoleKeys, step2ObservationCodes } =
		buildCompetencyObservationContext({
			predefinedObs,
			newObs,
			formObservationCodes,
		});

	const countsHowMany = countEIByCompetencyHowMany(slots);
	const countsHowOften = countEIByCompetencyHowOften({
		predefinedObs,
		newObs,
		predefinedCountByCompetency,
		predefinedRoleKeys,
		step2ObservationCodes,
	});
	const rules = upliftRules ?? DEFAULT_UPLIFT_LOOKUP_RULES;
	const competencies = new Set([...countsHowMany.keys(), ...countsHowOften.keys()]);
	const pairs: CompetencyRatingPair[] = [];

	for (const competency of competencies) {
		const howMany = countsHowMany.get(competency) ?? EMPTY_EI_COUNTS;
		const howOften = countsHowOften.get(competency) ?? EMPTY_EI_COUNTS;
		pairs.push({
			obCategoryCode: competency,
			ratingA: finalScoreFromCounts(competency, howMany, predefinedCountByCompetency, rules),
			ratingB: finalScoreFromCounts(competency, howOften, predefinedCountByCompetency, rules),
		});
	}

	return { pairs, howOftenCounts: countsHowOften };
}

export function calculateCompetencyRatings(params: {
	predefinedObs: IObservationRating[];
	newObs: IObservationRating[];
	formObservationCodes: Set<string>;
	upliftRules?: UpliftLookupRule[];
}): CompetencyRatingPair[] {
	return calculateCompetencyRatingDetails(params).pairs;
}

export function averageCompetencyRatings(
	pairs: { obCategoryCode: string; ratingA: number; ratingB: number }[],
	field: "ratingA" | "ratingB",
): number {
	if (!pairs.length) return 1;
	const sum = pairs.reduce((acc, row) => acc + row[field], 0);
	const avg = Math.round(sum / pairs.length);
	return Number.isNaN(avg) ? 1 : avg;
}
